
import { Lead } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { getLeadById } from './queries/getLeadById';
import { mapDbLeadToLead } from './mappers/leadMapper';

// PERFORMANCE: Removed local storage fallbacks - let errors propagate

// Update a lead
export const updateLead = async (updatedLead: Lead): Promise<Lead> => {
  try {
    // Update the lead in Supabase
    const { error } = await supabase
      .from('leads')
      .update({
        name: updatedLead.name,
        address: updatedLead.address,
        phone: updatedLead.phone,
        website: updatedLead.website,
        email: updatedLead.email,
        image_url: updatedLead.imageUrl,
        rating: updatedLead.rating,
        review_count: updatedLead.reviewCount,
        category: updatedLead.category,
        is_favorite: updatedLead.isFavorite,
        place_id: updatedLead.placeId,
        company_id: updatedLead.companyId,
        updated_at: new Date().toISOString(),
      } as any)
      .eq('id', updatedLead.id);

    if (error) throw error;
    
    return updatedLead;
  } catch (error) {
    console.error('Error updating lead in Supabase:', error);
    throw error;
  }
};

// Delete a lead
export const deleteLead = async (id: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Delete the lead from Supabase
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting lead from Supabase:', error);
      
      // Check if this is a permission error from RLS
      if (error.code === '42501' || error.message.includes('permission denied')) {
        return {
          success: false,
          error: 'Permission Denied: You can only delete leads that you have created.'
        };
      }
      
      // For other errors
      return {
        success: false,
        error: `Failed to delete lead: ${error.message}`
      };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error in deleteLead:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
};

// Toggle favorite status
export const toggleFavorite = async (id: string): Promise<Lead> => {
  try {
    // Get the current lead
    const lead = await getLeadById(id);
    
    if (!lead) {
      throw new Error('Lead not found');
    }
    
    // Toggle the favorite status
    const updatedFavorite = !lead.isFavorite;
    
    // Update in Supabase
    const { error } = await supabase
      .from('leads')
      .update({
        is_favorite: updatedFavorite,
        updated_at: new Date().toISOString(),
      } as any)
      .eq('id', id);

    if (error) throw error;
    
    // Return the updated lead
    return {
      ...lead,
      isFavorite: updatedFavorite,
    };
  } catch (error) {
    console.error('Error toggling favorite in Supabase:', error);
    throw error;
  }
};

// Create a new lead
export const createLead = async (leadData: Omit<Lead, 'id' | 'notes' | 'documents' | 'createdAt' | 'images'>, companyId?: string): Promise<Lead> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    // Get user's company if not explicitly provided
    let effectiveCompanyId = companyId;
    if (!effectiveCompanyId) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();
        
      effectiveCompanyId = profileData?.company_id;
    }

    // Create the lead with the company ID
    const { data, error } = await supabase
      .from('leads')
      .insert({
        name: leadData.name,
        address: leadData.address,
        phone: leadData.phone,
        website: leadData.website,
        email: leadData.email,
        image_url: leadData.imageUrl,
        rating: leadData.rating,
        review_count: leadData.reviewCount,
        category: leadData.category,
        is_favorite: leadData.isFavorite || false,
        place_id: leadData.placeId,
        company_id: effectiveCompanyId,
        created_by: userId || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating lead:', error);
      throw error;
    }

    return mapDbLeadToLead(data);
  } catch (error) {
    console.error('Error in createLead:', error);
    throw error;
  }
};
